import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type {
  CompetitorPlan,
  FieldUpdatePayload,
  FieldUpdateSuggestion,
  PlanGenerateInput,
} from "../types"

import {
  generateCompetitorPlan,
  getCompetitorPlan,
  patchCompetitorField,
  requestCompetitorFieldUpdate,
  saveNolatoRelationships,
} from "../api/ccp-api"
import { normalizeCompetitorPlan, resolveCompetitorCurrent } from "../plan-normalize"

export const competitorPlanQueryKey = (segmentId: string, companyId: string) =>
  ["sa-competitor-plan", segmentId, companyId] as const

function isNotFound(err: unknown): boolean {
  return (err as { response?: { status?: number } }).response?.status === 404
}

async function getPlanOrNull(segmentId: string, companyId: string): Promise<CompetitorPlan | null> {
  try {
    return normalizeCompetitorPlan(await getCompetitorPlan(segmentId, companyId))
  } catch (err) {
    if (isNotFound(err)) return null
    throw err
  }
}

export function useCompetitorPlan(segmentId: string, companyId: string) {
  const queryClient = useQueryClient()
  const key = competitorPlanQueryKey(segmentId, companyId)

  const planQuery = useQuery({
    queryKey: key,
    queryFn: () => getPlanOrNull(segmentId, companyId),
    enabled: Boolean(segmentId && companyId),
  })

  const generateMutation = useMutation({
    mutationFn: async (input: PlanGenerateInput = {}) =>
      normalizeCompetitorPlan(await generateCompetitorPlan(segmentId, companyId, input)),
    onSuccess: (plan) => queryClient.setQueryData(key, plan),
  })

  const requestUpdate = useMutation({
    mutationFn: async (fieldKey: string): Promise<FieldUpdateSuggestion> => {
      // Scope the suggestion to the same area the plan was generated for —
      // the backend threads `area` into evidence resolution, and this call
      // used to post an empty body so it was always undefined.
      const area = queryClient.getQueryData<CompetitorPlan>(key)?.meta.area
      const res = await requestCompetitorFieldUpdate(segmentId, companyId, {
        fieldKey,
        input: area ? { area } : {},
      })
      if (res.current == null) {
        return {
          ...res,
          current: resolveCompetitorCurrent(
            queryClient.getQueryData<CompetitorPlan>(key),
            fieldKey
          ),
        }
      }
      return res
    },
  })

  const patchField = useMutation({
    mutationFn: async (edit: { fieldKey: string; payload: FieldUpdatePayload }) =>
      normalizeCompetitorPlan(await patchCompetitorField(segmentId, companyId, edit)),
    onSuccess: (plan) => queryClient.setQueryData(key, plan),
  })

  /** Manual §06 Save — dedicated PUT (the only write path for nolatoRelationships). */
  const saveManual = useMutation({
    mutationFn: async (payload: FieldUpdatePayload) =>
      normalizeCompetitorPlan(await saveNolatoRelationships(segmentId, companyId, payload)),
    onSuccess: (plan) => queryClient.setQueryData(key, plan),
  })

  return { planQuery, generateMutation, requestUpdate, patchField, saveManual }
}
